import { useState, useRef, useEffect, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import { Html5Qrcode } from 'html5-qrcode'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  locale: string
  event: { id: number; name: string }
  urls: {
    checkIn: string
    eventUsers: string
  }
  translations: {
    checkIn: string
    scanQrCode: string
    scanning: string
    checkedInSuccess: string
    notFound: string
    ticketType: string
    bookingDate: string
    bookingTime: string
    close: string
    scanAnother: string
    manualEntry: string
    search: string
  }
}

interface ScanResult {
  found: boolean
  name?: string
  userType?: string | null
  date?: string | null
  time?: string | null
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function CheckIn({ locale, event, urls, translations: t }: Props) {
  const dir = locale === 'ar' ? 'rtl' : 'ltr'
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const readerRef = useRef<HTMLDivElement>(null)

  const [scanning, setScanning] = useState(false)
  const [result, setResult] = useState<ScanResult | null>(null)
  const [manualCode, setManualCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [showManual, setShowManual] = useState(false)

  /* ----- Scanner lifecycle ----- */
  const startScanner = useCallback(async () => {
    if (scannerRef.current || !readerRef.current) return
    setResult(null)
    setScanning(true)

    try {
      const scanner = new Html5Qrcode('qr-reader')
      scannerRef.current = scanner
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          lookupCode(decodedText)
          stopScanner()
        },
        () => {},
      )
    } catch {
      setScanning(false)
    }
  }, [])

  const stopScanner = useCallback(() => {
    const scanner = scannerRef.current
    if (scanner) {
      scanner.stop().catch(() => {})
      scanner.clear()
      scannerRef.current = null
    }
    setScanning(false)
  }, [])

  useEffect(() => () => stopScanner(), [stopScanner])

  /* ----- API call ----- */
  async function lookupCode(code: string) {
    if (!code.trim()) return
    setLoading(true)
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''

      const res = await fetch(`${urls.checkIn}?search=${encodeURIComponent(code.trim())}`, {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      })
      const data: ScanResult = await res.json()
      setResult(data)
    } catch {
      setResult({ found: false })
    } finally {
      setLoading(false)
    }
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault()
    lookupCode(manualCode)
    setManualCode('')
  }

  function handleScanAnother() {
    setResult(null)
    startScanner()
  }

  return (
    <>
      <Head title={`${t.checkIn} — ${event.name}`} />

      <div dir={dir} className="flex min-h-screen flex-col items-center bg-gray-50">
        {/* Header */}
        <div className="w-full bg-white shadow-sm">
          <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-4">
            <h1 className="text-lg font-semibold text-gray-900">{event.name}</h1>
            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
              {t.checkIn}
            </span>
          </div>
        </div>

        <div className="mx-auto w-full max-w-lg px-4 py-8">
          {/* Result modal */}
          {result && (
            <div className="mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <div
                className={`rounded-xl p-6 text-center shadow-md ${
                  result.found ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}
              >
                {result.found ? (
                  <>
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                      <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-green-800">{result.name}</p>
                    <p className="mt-1 text-sm text-green-600">{t.checkedInSuccess}</p>
                    {result.userType && (
                      <p className="mt-2 text-sm font-medium text-green-700">
                        {t.ticketType}: {result.userType}
                      </p>
                    )}
                    {(result.date || result.time) && (
                      <div className="mt-3 flex justify-center gap-4 text-xs text-green-700">
                        {result.date && (
                          <span>{t.bookingDate}: {result.date}</span>
                        )}
                        {result.time && (
                          <span>{t.bookingTime}: {result.time}</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                      <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-red-800">{t.notFound}</p>
                  </>
                )}

                <div className="mt-5 flex justify-center gap-3">
                  <button
                    onClick={handleScanAnother}
                    className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                  >
                    {t.scanAnother}
                  </button>
                  <button
                    onClick={() => setResult(null)}
                    className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {t.close}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Scanner */}
          {!result && (
            <div className="rounded-xl bg-white p-6 shadow-md">
              <div className="mb-4 text-center">
                <p className="text-sm font-medium text-gray-700">
                  {scanning ? t.scanning : t.scanQrCode}
                </p>
              </div>

              {/* QR reader container */}
              <div
                id="qr-reader"
                ref={readerRef}
                className="mx-auto overflow-hidden rounded-lg"
                style={{ maxWidth: 350 }}
              />

              {!scanning && (
                <button
                  onClick={startScanner}
                  className="mt-4 w-full rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                >
                  {t.scanQrCode}
                </button>
              )}

              {scanning && (
                <button
                  onClick={stopScanner}
                  className="mt-4 w-full rounded-lg border border-gray-300 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t.close}
                </button>
              )}

              {/* Manual entry toggle */}
              <div className="mt-4 text-center">
                <button
                  onClick={() => { setShowManual(!showManual); stopScanner() }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {t.manualEntry}
                </button>
              </div>

              {showManual && (
                <form onSubmit={handleManualSubmit} className="mt-4 flex gap-2">
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Token / Code"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                  <button
                    type="submit"
                    disabled={loading || !manualCode.trim()}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? '...' : t.search}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
